import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  TrendingUp, 
  Calendar, 
  MapPin, 
  Download, 
  Filter,
  Search,
  BarChart3,
  PieChart,
  Globe,
  FileSpreadsheet,
  RefreshCw,
  Eye,
  Mail,
  Phone,
  LogOut
} from 'lucide-react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import AdminLogin from '../components/admin/AdminLogin';
import AdminSetup from '../components/admin/AdminSetup';
import ScheduleExportModal from '../components/admin/ScheduleExportModal';
import { useAdminStore } from '../store/adminStore';
import UserDetailsModal from '../components/admin/UserDetailsModal';
import AnalyticsChart from '../components/admin/AnalyticsChart';
import LocationHeatmap from '../components/admin/LocationHeatmap';
import ExportToSheets from '../components/admin/ExportToSheets';

const AdminDashboard: React.FC = () => {
  const { 
    users, 
    analytics, 
    isLoading, 
    isAuthenticated,
    setAuthenticated,
    fetchUsers, 
    fetchAnalytics,
    exportToGoogleSheets,
    scheduleDataExport
  } = useAdminStore();
  
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('30');
  const [isExporting, setIsExporting] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Check if user email is an admin email
        const adminEmails = ['admin@lsem.org.ng', 'info@lsem.org.ng'];
        if (adminEmails.includes(user.email || '')) {
          setAuthenticated(true);
          fetchUsers();
          fetchAnalytics();
        } else {
          // Not an admin, sign out
          signOut(auth);
          setAuthenticated(false);
        }
      } else {
        setAuthenticated(false);
      }
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [setAuthenticated, fetchUsers, fetchAnalytics]);

  const handleLogin = () => {
    // Authentication state will be handled by onAuthStateChanged
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.businessType.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (user.results && user.results.overallStatus.toLowerCase().replace(' ', '-') === statusFilter);
    
    return matchesSearch && matchesStatus;
  });

  const handleExportToSheets = async () => {
    setIsExporting(true);
    try {
      await exportToGoogleSheets();
      // Show success message
    } catch (error) {
      console.error('Export failed:', error);
      // Show error message
    } finally {
      setIsExporting(false);
    }
  };

  const handleScheduleExport = async (frequency: 'weekly' | 'monthly', day: string, time: string) => {
    try {
      await scheduleDataExport(frequency, day, time);
      setIsScheduleModalOpen(false);
    } catch (error) {
      console.error('Schedule export failed:', error);
    }
  };

  // Show loading while checking auth
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show setup if requested
  if (showSetup) {
    return <AdminSetup onSetupComplete={handleSetupComplete} />;
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div>
        <AdminLogin onLoginSuccess={handleLogin} />
        <div className="fixed bottom-4 right-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSetup(true)}
            className="bg-white shadow-lg"
          >
            Need to setup admin accounts?
          </Button>
        </div>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Users',
      value: analytics.totalUsers.toLocaleString(),
      change: `+${analytics.userGrowth.thisMonth}% this month`,
      icon: Users,
      color: 'bg-primary-500'
    },
    {
      title: 'Completed Tests',
      value: analytics.completedTests.toLocaleString(),
      change: `${Math.round((analytics.completedTests / analytics.totalUsers) * 100)}% completion rate`,
      icon: BarChart3,
      color: 'bg-success-500'
    },
    {
      title: 'Average Score',
      value: `${analytics.averageScore}%`,
      change: `${analytics.scoreImprovement > 0 ? '+' : ''}${analytics.scoreImprovement}% vs last month`,
      icon: TrendingUp,
      color: 'bg-accent-500'
    },
    {
      title: 'Export Ready',
      value: `${analytics.readinessDistribution.ready}%`,
      change: `${analytics.readinessDistribution.ready} of ${analytics.completedTests} users`,
      icon: Globe,
      color: 'bg-secondary-500'
    }
  ];

  return (
    <Layout>
      <div className="container-custom py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">
              Comprehensive analytics and user management for Export Readiness Test
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              leftIcon={<RefreshCw />}
              onClick={() => {
                fetchUsers();
                fetchAnalytics();
              }}
              disabled={isLoading}
            >
              Refresh Data
            </Button>
            
            <Button
              variant="outline"
              leftIcon={<Calendar />}
              onClick={() => setIsScheduleModalOpen(true)}
            >
              Schedule Export
            </Button>
            
            <Button
              variant="primary"
              leftIcon={<FileSpreadsheet />}
              onClick={handleExportToSheets}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export to Sheets'}
            </Button>

            <Button
              variant="outline"
              leftIcon={<LogOut />}
              onClick={handleLogout}
              className="text-error-600 border-error-300 hover:bg-error-50"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={stat.title}
                className="bg-white rounded-xl shadow-card p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {stat.value}
                </h3>
                <p className="text-sm font-medium text-gray-600 mb-2">
                  {stat.title}
                </p>
                <p className="text-xs text-gray-500">
                  {stat.change}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <motion.div
            className="bg-white rounded-xl shadow-card p-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800">User Growth Trends</h3>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
            </div>
            <AnalyticsChart data={analytics.userGrowthData} type="line" />
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-card p-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">Readiness Distribution</h3>
            <AnalyticsChart 
              data={[
                { name: 'Ready', value: analytics.readinessDistribution.ready, color: '#22c55e' },
                { name: 'Almost Ready', value: analytics.readinessDistribution.almostReady, color: '#f59e0b' },
                { name: 'Not Ready', value: analytics.readinessDistribution.notReady, color: '#ef4444' }
              ]} 
              type="pie" 
            />
          </motion.div>
        </div>

        {/* Location Heatmap */}
        <motion.div
          className="bg-white rounded-xl shadow-card p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">User Distribution by Location</h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4" />
              <span>{analytics.locationData.length} locations</span>
            </div>
          </div>
          <LocationHeatmap data={analytics.locationData} />
        </motion.div>

        {/* User Management Section */}
        <motion.div
          className="bg-white rounded-xl shadow-card p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <h3 className="text-lg font-bold text-gray-800">User Management</h3>
            
            <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              <div className="relative flex-1 lg:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full lg:w-64"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="ready">Ready</option>
                <option value="almost-ready">Almost Ready</option>
                <option value="not-ready">Not Ready</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Business</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Location</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Score</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{user.fullName}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{user.businessType}</div>
                        <div className="text-sm text-gray-500">{user.role}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm">
                        <div>{user.state}</div>
                        <div className="text-gray-500">{user.country}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {user.results ? (
                        <div className="text-lg font-bold text-primary-600">
                          {Math.round(user.results.overallPercentage)}%
                        </div>
                      ) : (
                        <span className="text-gray-400">Not completed</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      {user.results ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.results.overallStatus === 'Ready' 
                            ? 'bg-success-100 text-success-700'
                            : user.results.overallStatus === 'Almost Ready'
                            ? 'bg-warning-100 text-warning-700'
                            : 'bg-error-100 text-error-700'
                        }`}>
                          {user.results.overallStatus}
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Incomplete
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-500">
                      {user.results 
                        ? new Date(user.results.completedAt).toLocaleDateString()
                        : new Date(user.createdAt).toLocaleDateString()
                      }
                    </td>
                    <td className="py-4 px-4">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<Eye />}
                        onClick={() => setSelectedUser(user)}
                      >
                        View
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No users have registered yet.'
                }
              </p>
            </div>
          )}
        </motion.div>

        {/* Export to Sheets Component */}
        <ExportToSheets />

        {/* User Details Modal */}
        {selectedUser && (
          <UserDetailsModal
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
          />
        )}

        {/* Schedule Export Modal */}
        <ScheduleExportModal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
        />
      </div>
    </Layout>
  );
};

export default AdminDashboard;